import { ref, nextTick } from '@vue/composition-api';
import { CheckKeywordResponse } from '@tt4b/ttam-enums';
import { isEqual, uniqWith, chunk, flatten } from 'lodash-es';
import { useCheckGetWordIdsApi } from '@tt4b/ttam-hooks';
import { KeywordInvalidCode, SearchAdsKeywordItem } from '@ad/tt_ads_common_enums';
import { getDefaultKeywordItem, getRepeatItemArray, getMapByKey, getKeywordItem, trackEvent } from '@tt4b/ttam-utils';
import { FormItem, Input } from 'byted-vui';

// eslint-disable-next-line no-unused-vars
type OnKeywordsChange = (keywordList: SearchAdsKeywordItem[]) => void;

export function useSearchKeywords(onKeywordsChange?: OnKeywordsChange) {
  const searchKeywordRefs = ref([]);
  const inputRefs = ref([]);
  const repeatKeywords = ref<string[]>([]);
  const keywordList = ref<SearchAdsKeywordItem[]>([getDefaultKeywordItem()]);

  function onKeywordListChange() {
    if (onKeywordsChange) {
      onKeywordsChange(keywordList.value);
    }
  }

  function validateKeywordItem(index: number) {
    if ((searchKeywordRefs.value[index] as FormItem)?.validate) {
      (searchKeywordRefs.value[index] as FormItem)?.validate();
    }
  }

  function mergeKeywordList(keywordItemList: Partial<SearchAdsKeywordItem>[]) {
    keywordItemList.forEach(keywordItem => {
      const { id } = keywordItem;
      const currentKeywordItemIndex = keywordList.value.findIndex(item => item.id === id);
      const currentKeywordItem = keywordList.value[currentKeywordItemIndex];

      if (currentKeywordItem) {
        const mergedKeywordItem = Object.assign({}, currentKeywordItem, keywordItem);
        keywordList.value.splice(currentKeywordItemIndex, 1, mergedKeywordItem);
        onKeywordListChange();
        nextTick(() => {
          validateKeywordItem(currentKeywordItemIndex);
        });
      }
    });
  }

  function deleteKeywordList(startIndex: number, num = keywordList.value.length - startIndex) {
    keywordList.value.splice(startIndex, num);
    onKeywordListChange();
  }

  function addKeywordList(startIndex: number, keywordItemList: SearchAdsKeywordItem[]) {
    keywordList.value.splice(startIndex, 0, ...keywordItemList);
    onKeywordListChange();
  }

  /** 关键词依据分隔符获取所有独立的关键词 */
  function getAllKeywords(keywords: string[]) {
    return keywords.reduce((list: string[], keyword: string) => {
      const keywordSplitList = keyword.split(/[,;\n]/);

      return list.concat(keywordSplitList);
    }, []);
  }

  /** 发生变更时聚焦在最后一个变更的input上 */
  function inputFocusOnLastKeywordItem(keywordItemList: SearchAdsKeywordItem[]) {
    nextTick(() => {
      const lastKeywordItem = keywordItemList[keywordItemList.length - 1];
      if (lastKeywordItem) {
        try {
          const keywordItemInput = inputRefs.value.find(item => item.$attrs.id === lastKeywordItem.id);
          if (keywordItemInput) {
            (keywordItemInput.$refs.input as Input).focus();
          }
        } catch (error) {}
      }
    });
  }

  function handleRepeatKeywords(keywords: string[]) {
    const currentKeywords = keywordList.value.map(item => item.keyword);
    const allKeywords = keywords.concat(currentKeywords);
    /** 记录重复的关键词 */
    repeatKeywords.value = getRepeatItemArray(allKeywords);
    const currentKeywordSet = new Set(currentKeywords);
    const selfUniqKeywords = uniqWith(
      keywords,
      (preKeyword, currentKeyword) => currentKeyword && isEqual(preKeyword, currentKeyword),
    );
    const uniqKeywords = selfUniqKeywords.filter(keyword => !keyword || !currentKeywordSet.has(keyword));

    return uniqKeywords;
  }

  async function getCheckedKeywordItemList(keywordItemList: SearchAdsKeywordItem[], pureKeywords: string[]) {
    try {
      const chunkList = chunk(pureKeywords, 200);
      const chunkParallelList = chunk(chunkList, 3);
      const checkedResult: CheckKeywordResponse[] = [];

      for (const requestList of chunkParallelList) {
        const result = await Promise.all(requestList.map(keywords => useCheckGetWordIdsApi({ keywords })));
        checkedResult.push(...flatten(result));
      }

      const checkedResultMap = getMapByKey(checkedResult, 'keyword');
      return keywordItemList.map(item => {
        const { pureKeyword } = item;
        const checkedRes = checkedResultMap[pureKeyword];

        /** 接口调用完成时关键词发生变更则不变更 */
        if (!checkedRes || checkedRes.keyword !== item.pureKeyword) {
          const currentKeywordItem = keywordList.value.find(innerItem => innerItem.id === item.id);

          if (currentKeywordItem) {
            return {
              id: currentKeywordItem.id,
              keywordId: currentKeywordItem.keywordId,
              invalidCode: currentKeywordItem.invalidCode,
            };
          }

          return {
            id: item.id,
            keywordId: item.keywordId,
            invalidCode: item.invalidCode,
          };
        }

        return {
          id: item.id,
          keywordId: checkedRes.keywordId || '',
          invalidCode: checkedRes.invalidCode || KeywordInvalidCode.NONE,
        };
      });
    } catch {
      const eventParam = {
        std_component_name: 'ttam_adgroup_keywords_system_error',
        keywords: keywordItemList.map(item => item.keyword).join(','),
      };

      trackEvent('creation_component_show', eventParam);
      return keywordItemList.map(item => ({
        id: item.id,
        keywordId: '',
        invalidCode: KeywordInvalidCode.SYSTEM_ERROR,
      }));
    }
  }

  async function handleCheckKeywords(keywordItemList: SearchAdsKeywordItem[]) {
    const pureKeywords = keywordItemList.map(item => item.pureKeyword);
    const nonEmptyKeywords = pureKeywords.filter(Boolean);

    if (!nonEmptyKeywords.length) {
      return;
    }

    /** 设置loading，invalidCode设置为SYSTEM_ERROR，如果此时提交则触发 */
    mergeKeywordList(keywordItemList.map(item => ({ id: item.id, loading: true })));

    /** 获取check result，内部处理了try catch */
    const checkedKeywordItemList = await getCheckedKeywordItemList(keywordItemList, nonEmptyKeywords);

    mergeKeywordList(checkedKeywordItemList.map(item => ({ ...item, loading: false })));
  }

  function checkIsKeywordRepeat(index: number, keyword: string) {
    /** 空字符串不记作重复 */
    if (!keyword) {
      return false;
    }

    const otherKeywords = keywordList.value
      .filter((item, keywordIndex) => keywordIndex !== index)
      .map(item => item.keyword);

    return otherKeywords.includes(keyword);
  }

  function handleKeywordChange(index: number, keyword: string) {
    const isRepeat = checkIsKeywordRepeat(index, keyword);

    if (isRepeat) {
      handleRepeatKeywords([keyword]);
      deleteKeywordList(index, 1);
      return;
    }

    /** 保留id */
    const currentKeywordItem = keywordList.value[index];
    const keywordItem = Object.assign({}, getKeywordItem(keyword), { id: currentKeywordItem.id });
    const keywordItemList = [keywordItem];

    mergeKeywordList(keywordItemList);

    /** 获取关键词id、校验信息 */
    handleCheckKeywords(keywordItemList);
  }

  function batchAddKeywords(startIndex: number, keywords: string[]) {
    if (!keywords.length) {
      return;
    }
    const allKeywords = getAllKeywords(keywords);

    /** 处理重复关键词 */
    const uniqKeywords = handleRepeatKeywords(allKeywords);
    const keywordItemList = uniqKeywords.map(keyword => getKeywordItem(keyword));

    addKeywordList(startIndex, keywordItemList);
    /** 新增的最后一个词聚焦 */
    inputFocusOnLastKeywordItem(keywordItemList);

    /** 获取关键词id、校验信息 */
    handleCheckKeywords(keywordItemList);
  }

  function handleInputKeywords(index: number, val: string) {
    const allKeywords = getAllKeywords([val]);
    const currentKeyword = allKeywords[0];

    const isRepeat = checkIsKeywordRepeat(index, currentKeyword);

    handleKeywordChange(index, currentKeyword);

    const nextIndex = isRepeat ? index : index + 1;
    batchAddKeywords(nextIndex, allKeywords.slice(1));
  }

  function addKeyword() {
    batchAddKeywords(keywordList.value.length, ['']);
  }

  function clearAllKeywords() {
    keywordList.value = [];
    onKeywordListChange();

    /** 最少包含一个keyword input */
    addKeyword();
  }

  return {
    keywordList,
    repeatKeywords,
    searchKeywordRefs,
    inputRefs,
    handleInputKeywords,
    deleteKeywordList,
    batchAddKeywords,
    inputFocusOnLastKeywordItem,
    clearAllKeywords,
    addKeyword,
    validateKeywordItem,
    getCheckedKeywordItemList,
  };
}
